/**
 * LicenceStatusNotice - the licence state, in the side nav, for every page.
 *
 * Shows nothing while every module is in term. Otherwise one line per module
 * whose licence is in its grace period, read only, or cancelled - the reason a
 * change the user tries will be refused with 402 (see lib/licence-notices.ts).
 * Collapsed, it is a single marker whose tooltip carries the same text.
 */

import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/cn';
import { licenceNotices, type ModuleLicence } from '@/lib/licence-notices';

export function LicenceStatusNotice({
  licences,
  collapsed,
}: {
  licences: ReadonlyArray<ModuleLicence> | undefined;
  collapsed: boolean;
}) {
  const notices = licenceNotices(licences);
  if (notices.length === 0) return null;
  const danger = notices.some((n) => n.tone === 'danger');
  const summary = notices.map((n) => `${n.title}. ${n.detail}`).join('\n');

  if (collapsed) {
    return (
      <div className="flex justify-center py-2 border-b border-border/40" role="status" title={summary}>
        <AlertTriangle
          className={cn('h-4 w-4', danger ? 'text-destructive' : 'text-warning')}
          aria-label={summary}
        />
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-b border-border/40 space-y-1.5" role="status" aria-live="polite">
      {notices.map((n) => (
        <div
          key={n.product}
          className={cn(
            'rounded-md border px-2.5 py-1.5 text-xs leading-snug',
            n.tone === 'danger'
              ? 'border-destructive/40 bg-destructive/5'
              : 'border-warning/40 bg-warning/5',
          )}
        >
          <div
            className={cn(
              'flex items-center gap-1.5 font-medium',
              n.tone === 'danger' ? 'text-destructive' : 'text-warning',
            )}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>{n.title}</span>
          </div>
          <p className="mt-0.5 text-muted-foreground">{n.detail}</p>
        </div>
      ))}
    </div>
  );
}
